/**
 * flowState — die Zustands-Grammatik als reine Funktionen.
 *
 * Fünf Formen (`ok` · `running` · `failed` · `sealed` · `open`) plus zwei
 * Nicht-Ereignisse (`skipped` · `ghost`). Alles, was gerechnet wird — Rohwert-
 * Abbildung, Roll-up-Priorität, Aggregat und Klartext — steht hier und nicht in
 * einer Komponente, damit es ohne DOM testbar ist.
 *
 * Zwei Vokabulare auf einer Formsprache (design-hig.md, „Die Zustands-Grammatik"):
 * `run` = Laufzustand (Runs · Queue · Artefakte), `build` = Bauzustand (Architektur).
 */
import { STATE_PRIORITY, type FlowState } from "@/types/pipeline";

/** Alle Zustände in der Reihenfolge, in der sie im Aggregat-Ring erscheinen. */
export const FLOW_STATES: readonly FlowState[] = [
  "ok",
  "running",
  "failed",
  "sealed",
  "open",
  "skipped",
  "ghost",
] as const;

export type FlowVocabulary = "run" | "build" | "artifact";

/** Laufzustand — Runs und Queue. */
const RUN_WORD: Record<FlowState, string> = {
  ok: "erledigt",
  running: "läuft",
  failed: "fehlgeschlagen",
  sealed: "wartet auf Menschen",
  open: "noch nicht erreicht",
  skipped: "übersprungen",
  ghost: "trifft nicht zu",
};

/** Bauzustand — Architektur. Gleiche Formen, anderes Wort. */
const BUILD_WORD: Record<FlowState, string> = {
  ok: "in Betrieb",
  running: "im Aufbau",
  failed: "fehlgeschlagen",
  sealed: "extern, fremdbestimmt",
  open: "Stub",
  skipped: "übersprungen",
  ghost: "trifft nicht zu",
};

/**
 * Artefakt-Stand — Artefakte-Seite (design-hig.md ③).
 *
 * Ein Slot ist hier kein Arbeitsschritt, sondern ein Formatstand. Dieselben
 * Formen, aber die Laufzustand-Wörter sind schlicht falsch: ein Siegel bedeutet
 * hier NICHT „wartet auf Menschen", sondern die Konsultationsfassung — 202710
 * ist durchgehend eine solche, und für Screenreader trägt das Wort die ganze
 * Aussage. Ebenso ist ein leerer Ring kein „noch nicht erreicht", sondern ein
 * Stand, den es gibt, der aber 0 Artefakte führt; die Ghost-Linie steht für
 * einen Typ, den es in diesem Stand gar nicht gibt (`bnetza-festlegung`,
 * `bo4e-schema` mit ihren Sonderständen).
 */
const ARTIFACT_WORD: Record<FlowState, string> = {
  ok: "vorhanden",
  running: "wird gerade erzeugt",
  failed: "fehlgeschlagen",
  sealed: "Konsultationsfassung",
  open: "vorhanden, aber leer",
  skipped: "bewusst übersprungen",
  ghost: "kein Stand für diesen Typ",
};

const VOCABULARY: Record<FlowVocabulary, Record<FlowState, string>> = {
  run: RUN_WORD,
  build: BUILD_WORD,
  artifact: ARTIFACT_WORD,
};

/** Klartext eines Zustands im gewählten Vokabular. */
export function stateWord(state: FlowState, vocabulary: FlowVocabulary = "run"): string {
  return (VOCABULARY[vocabulary] ?? RUN_WORD)[state];
}

/**
 * Rohwerte aus `runs.json` / Jobs → Grammatik.
 *
 * `null`/`undefined`/leer bedeutet „diese Stage gibt es in diesem Objekt gar
 * nicht" → Ghost. Ein unbekannter, aber gesetzter Rohwert ist konservativ
 * `open` („noch nicht erreicht") — er verschwindet damit nicht stillschweigend.
 */
const RAW_STATE: Record<string, FlowState> = {
  success: "ok",
  succeeded: "ok",
  ok: "ok",
  done: "ok",
  completed: "ok",
  finished: "ok",
  running: "running",
  in_progress: "running",
  started: "running",
  active: "running",
  failed: "failed",
  failure: "failed",
  error: "failed",
  cancelled: "failed",
  canceled: "failed",
  awaiting_user: "sealed",
  awaiting_input: "sealed",
  blocked: "sealed",
  consultation: "sealed",
  external: "sealed",
  pending: "open",
  queued: "open",
  waiting: "open",
  planned: "open",
  stub: "open",
  skipped: "skipped",
  skip: "skipped",
  ghost: "ghost",
  not_applicable: "ghost",
};

export function toFlowState(raw: string | null | undefined): FlowState {
  if (raw == null || raw === "") return "ghost";
  return RAW_STATE[raw.toLowerCase()] ?? "open";
}

/**
 * Roll-up-Priorität: `failed > sealed > running > open > ok`.
 *
 * Die beiden Nicht-Ereignisse stehen dahinter — eine Zeile, in der nur
 * übersprungen/nicht anwendbar steht, rollt zu genau dem auf und nicht zu „ok".
 * (Übernommen aus dem Entwurf „dense": beantwortet „welche Zeile ist kaputt"
 * vor dem Kettenlesen.)
 *
 * Die Reihenfolge steht EINMAL im Datenvertrag (`STATE_PRIORITY`) — hier wird
 * sie nur weitergereicht, damit Datenschritt und Oberfläche nicht auseinander-
 * laufen können.
 */
export const ROLLUP_ORDER: readonly FlowState[] = STATE_PRIORITY;

/** Der führende Zustand einer Menge. Leere Menge → `ghost`. */
export function rollUpState(states: Iterable<FlowState>): FlowState {
  const seen = new Set<FlowState>(states);
  if (seen.size === 0) return "ghost";
  for (const s of ROLLUP_ORDER) if (seen.has(s)) return s;
  return "ghost";
}

export type FlowTally = Partial<Record<FlowState, number>>;

/** Zählt Zustände zu einem Aggregat. */
export function tallyStates(states: Iterable<FlowState>): FlowTally {
  const out: FlowTally = {};
  for (const s of states) out[s] = (out[s] ?? 0) + 1;
  return out;
}

/** Summe eines Aggregats. */
export function tallyTotal(tally: FlowTally): number {
  return FLOW_STATES.reduce((sum, s) => sum + (tally[s] ?? 0), 0);
}

/**
 * Aggregat als Klartext („3 erledigt, 6 noch nicht erreicht") — für `aria-label`
 * und Tooltip. Reihenfolge ist die Ring-Reihenfolge, damit Text und Ring
 * dieselbe Geschichte erzählen.
 */
export function tallyWords(tally: FlowTally, vocabulary: FlowVocabulary = "run"): string {
  return FLOW_STATES.filter((s) => (tally[s] ?? 0) > 0)
    .map((s) => `${tally[s]} ${stateWord(s, vocabulary)}`)
    .join(", ");
}

/** Deutsche Zahlformatierung (Tausenderpunkt) — eine Stelle für die ganze Bahn. */
export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("de-DE");
}
